import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Icon } from "./Icon";

/**
 * A modal built on the native <dialog>.
 *
 * showModal() is doing the heavy lifting: focus is trapped, the rest of the
 * page is inert, and Escape closes it — accessibility we would otherwise have
 * to hand-roll and get subtly wrong. The dialog's own close event (Escape,
 * form method=dialog) is routed back through onClose so React state stays in
 * sync. A click on the backdrop deliberately does NOT close it: every dialog
 * here is a form someone is typing into, and a stray click outside it was
 * losing that work.
 */
export function Modal({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // showModal() gives modality, focus-trap and top-layer, but jsdom does not
    // implement it — fall back to the `open` attribute so tests still mount the
    // content and a real browser still gets the modal behaviour.
    if (open && !dialog.open) {
      try {
        dialog.showModal();
      } catch {
        dialog.setAttribute("open", "");
      }
    }
    if (!open && dialog.open) {
      try {
        dialog.close();
      } catch {
        dialog.removeAttribute("open");
      }
    }
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      aria-labelledby="modal-title"
      className="m-auto w-[min(560px,calc(100vw-32px))] rounded-xl border border-[var(--border)] bg-[var(--bg)] p-0 shadow-lg backdrop:bg-black/30"
    >
      <div className="flex items-center justify-between border-b border-[var(--border)] px-5 py-4">
        <h2 id="modal-title" className="text-md font-semibold">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-md p-1 text-[var(--text-2)] transition-colors hover:bg-gray-100 hover:text-[var(--text)] focus-visible:focus-ring"
        >
          <Icon name="x" />
        </button>
      </div>
      <div className="grid gap-4 p-5">{children}</div>
      {footer && <div className="flex justify-end gap-2 border-t border-[var(--border)] bg-gray-100 px-5 py-3">{footer}</div>}
    </dialog>
  );
}

/** The footer every create dialog ends with: Cancel, then the primary action. */
export function DialogActions({
  onCancel,
  onConfirm,
  confirmLabel,
  confirmDisabled = false,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  /** Keeps the confirm button off until the dialog has what it needs. */
  confirmDisabled?: boolean;
}) {
  return (
    <>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-md border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:focus-ring"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={confirmDisabled}
        className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:focus-ring disabled:opacity-50"
      >
        {confirmLabel}
      </button>
    </>
  );
}
